import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowDown } from "lucide-react";
import Nav, { scrollToId } from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal, MaskLine, EASE } from "@/components/motion";
import { ProjectWidget } from "@/components/widgets";
import { PROJECTS } from "@/data/projects";

const BriefCard = ({ label, text, tid, active, onActivate, dark }) => (
  <button
    onClick={onActivate}
    onMouseEnter={onActivate}
    data-testid={tid}
    aria-pressed={active}
    className={`p-6 text-left transition-colors duration-500 sm:p-8 ${
      active ? "bg-moss" : "bg-paper hover:bg-stone2"
    }`}
  >
    <p className={`text-[10px] font-semibold uppercase tracking-[0.22em] transition-colors duration-500 ${active ? "text-champagne" : "text-moss"}`}>
      {label}
    </p>
    <p className={`mt-3 text-sm leading-relaxed transition-colors duration-500 ${active ? "text-paper/90" : "text-smoke"}`}>
      {text}
    </p>
  </button>
);

const SectionRow = ({ num, label, sub, children, testid }) => (
  <div className="grid grid-cols-1 gap-6 border-t border-hairline py-14 lg:grid-cols-12" data-testid={testid}>
    <Reveal className="lg:col-span-4" y={20}>
      <p className="font-serif text-5xl font-light text-champagne" aria-hidden="true">{num}</p>
      <h2 className="mt-3 font-serif text-3xl font-light text-ink">{label}</h2>
      {sub && <p className="mt-1 text-sm italic text-fog">{sub}</p>}
    </Reveal>
    <div className="lg:col-span-7 lg:col-start-6">{children}</div>
  </div>
);

export default function ProjectPage() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const { scrollYProgress } = useScroll();
  const [briefActive, setBriefActive] = useState(0);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    setBriefActive(0);
  }, [slug]);

  if (!project) return <Navigate to="/" replace />;

  const idx = PROJECTS.indexOf(project);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  const briefFields = [
    ["The question", project.brief.question, "brief-question"],
    ["The evidence", project.brief.evidence, "brief-evidence"],
    ["The insight", project.brief.insight, "brief-insight"],
    ["The decision", project.brief.decision, "brief-decision"],
  ];

  return (
    <div className="bg-paper" data-testid={`project-page-${project.slug}`}>
      <motion.div
        className="fixed inset-x-0 top-0 z-[70] h-[3px] origin-left bg-moss"
        style={{ scaleX: scrollYProgress }}
        aria-hidden="true"
      />
      <Nav />

      <main className="pt-28 sm:pt-36">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
          <Reveal y={16}>
            <Link
              to="/work"
              data-testid="project-back-link"
              className="group inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-fog transition-colors hover:text-ink"
            >
              <ArrowLeft size={14} strokeWidth={1.5} className="transition-transform duration-300 group-hover:-translate-x-1" />
              Selected Work
            </Link>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal delay={0.05} y={16}>
                <p className="overline-label flex flex-wrap items-center gap-3">
                  <span className="text-moss">{project.num}</span>
                  <span className="inline-block h-px w-8 bg-current opacity-40" aria-hidden="true" />
                  {project.category}
                </p>
              </Reveal>
              <h1 className="mt-6 font-serif font-light leading-[1.02] tracking-tight text-ink text-4xl sm:text-6xl" data-testid="project-title">
                <MaskLine delay={0.15}>{project.title}</MaskLine>
              </h1>
              <Reveal delay={0.25} y={16}>
                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <p className="inline-block border border-champagne px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-smoke" data-testid="project-label">
                    {project.label}
                  </p>
                  <p className="font-serif text-lg italic text-fog">{project.questionLine}</p>
                </div>
              </Reveal>
              <Reveal delay={0.3} y={16}>
                <div className="mt-8 inline-block border-l-2 border-champagne pl-5">
                  <p className="font-serif text-3xl font-light text-moss" data-testid="project-metric">{project.metric.value}</p>
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-fog">{project.metric.label}</p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.2} y={30}>
                <div className="overflow-hidden border border-hairline">
                  <motion.img
                    src={project.heroImage}
                    alt={project.heroAlt}
                    initial={{ scale: 1.08 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.4, ease: EASE }}
                    className="image-treatment aspect-[4/3] w-full object-cover"
                    loading="eager"
                    data-testid="project-hero-image"
                  />
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1} className="mt-16">
            <div className="border border-hairline" data-testid="analyst-brief">
              <div className="flex items-center justify-between border-b border-hairline bg-stone2 px-6 py-4 sm:px-8">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-ink">60-second analyst brief</p>
                <button
                  onClick={() => scrollToId("full-analysis")}
                  data-testid="explore-analysis-link"
                  className="group inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-fog transition-colors hover:text-ink"
                >
                  Explore the analysis
                  <ArrowDown size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-px bg-hairline sm:grid-cols-2 lg:grid-cols-4">
                {briefFields.map(([label, text, tid], i) => (
                  <BriefCard
                    key={tid}
                    label={label}
                    text={text}
                    tid={tid}
                    active={briefActive === i}
                    onActivate={() => setBriefActive(i)}
                  />
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div id="full-analysis" className="mt-24 border-t border-hairline bg-stone2/50 sm:mt-32">
          <div className="mx-auto max-w-[1400px] px-6 py-24 sm:px-10 sm:py-32">
            <Reveal>
              <p className="overline-label mb-16 flex items-center gap-3">
                <span className="text-moss">Full analysis</span>
                <span className="inline-block h-px w-10 bg-current opacity-40" aria-hidden="true" />
                Six parts
              </p>
            </Reveal>

            <SectionRow num="01" label="Context & Question" sub="The situation, and the decision it demanded." testid="section-context">
              <Reveal y={20}>
                <p className="font-serif text-2xl sm:text-3xl font-light italic leading-snug text-ink">“{project.brief.question}”</p>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-smoke">{project.context}</p>
              </Reveal>
            </SectionRow>

            <SectionRow num="02" label="Approach" sub="How the problem was worked." testid="section-approach">
              <Reveal y={20}>
                <p className="text-base sm:text-lg leading-relaxed text-smoke">{project.approach}</p>
              </Reveal>
            </SectionRow>

            {project.gallery?.length > 0 && (
              <div className={`grid grid-cols-1 gap-px border border-hairline bg-hairline ${project.gallery.length > 1 ? "sm:grid-cols-2" : ""}`} data-testid="project-gallery">
                {project.gallery.map((g) => (
                  <figure key={g.src} className="group relative overflow-hidden bg-paper">
                    <img
                      src={g.src}
                      alt={g.alt}
                      loading="lazy"
                      className="image-treatment h-64 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] sm:h-80"
                    />
                    <figcaption className="border-t border-hairline px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-fog">
                      {g.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            <SectionRow num="03" label="Interactive Analysis" sub="The evidence — investigate it." testid="section-analysis">
              <Reveal y={20}>
                <ul className="mb-12 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                  {project.analysis.map((a) => (
                    <li key={a} className="border-t border-hairline py-3 text-sm leading-relaxed text-smoke">{a}</li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.1}>
                <ProjectWidget project={project} />
              </Reveal>
            </SectionRow>

            <SectionRow num="04" label="Insight" sub="What the evidence revealed." testid="section-insight">
              <Reveal y={20}>
                <p className="font-serif text-2xl sm:text-3xl font-light leading-snug text-ink">“{project.insightText}”</p>
              </Reveal>
            </SectionRow>

            <SectionRow num="05" label="Recommendation" sub="What should happen, and why." testid="section-recommendation">
              <Reveal y={20}>
                <div className="flex flex-col gap-px border border-hairline bg-hairline">
                  {project.recommendation.map((r, i) => (
                    <div key={r} className="flex items-baseline gap-5 bg-paper p-6">
                      <span className="font-serif text-2xl font-light text-champagne" aria-hidden="true">{i + 1}</span>
                      <p className="text-base leading-relaxed text-ink">{r}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </SectionRow>

            <SectionRow num="06" label="What This Demonstrates" sub="The capabilities behind the case." testid="section-demonstrates">
              <Reveal y={20}>
                <div className="flex flex-wrap gap-2.5">
                  {project.demonstrates.map((d) => (
                    <span
                      key={d}
                      data-testid={`demonstrates-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="border border-moss/40 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-moss"
                    >
                      {d}
                    </span>
                  ))}
                </div>
                <p className="mt-8 border-l-2 border-champagne pl-5 font-serif text-xl sm:text-2xl font-light italic leading-snug text-smoke">
                  {project.takeaway}
                </p>
              </Reveal>
            </SectionRow>

            <div className="border border-moss bg-moss p-8 sm:p-12" data-testid="next-steps">
              <Reveal y={20}>
                <p className="overline-label text-champagne/70">If I took this analysis further…</p>
                <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-3">
                  {project.nextSteps.map((s, i) => (
                    <div key={s} className="border-t border-paper/15 pt-4">
                      <p className="font-serif text-3xl font-light text-champagne" aria-hidden="true">0{i + 1}</p>
                      <p className="mt-2 text-sm leading-relaxed text-paper/85">{s}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <div className="border-t border-hairline">
          <Link
            to={`/work/${next.slug}`}
            data-testid="next-project-link"
            className="group mx-auto flex max-w-[1400px] flex-col gap-3 px-6 py-16 sm:flex-row sm:items-end sm:justify-between sm:px-10 sm:py-20"
          >
            <div>
              <p className="overline-label">Next case — {next.num}</p>
              <p className="mt-4 max-w-2xl font-serif text-3xl sm:text-5xl font-light leading-tight text-ink transition-colors duration-300 group-hover:text-moss">
                {next.title}
              </p>
            </div>
            <ArrowRight size={28} strokeWidth={1} className="text-champagne transition-all duration-300 group-hover:translate-x-2 group-hover:text-moss" aria-hidden="true" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
